'use client';
import { useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    Landmark, FileText, FolderOpen, Upload, Download, RefreshCw,
    CheckCircle, AlertTriangle, Sparkles, X, Server,
    Layers, Target, Users, Building2, Leaf
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout } from '@/components/AgentPageLayout';
import { AGENT_CONFIGS } from '@/lib/agent-constants';
import { AgentToolbar } from '@/components/AgentToolbar';

interface ParseResult {
    fileName: string; pages: number; textLength: number; textPreview: string;
}
interface FinishingMaterial {
    room: string; floor: string; wall: string; ceiling: string; page: number;
}
interface GreenCert {
    item: string; requirement: string; grade: string; page: number;
}
interface BfReq {
    item: string; requirement: string; page: number;
}
interface SpaceProg {
    room: string; area: string; requirement: string; page: number;
}
interface EvalKeyword {
    keyword: string; importance: string; context: string;
}
interface CriticalIssue {
    issue: string; detail: string; severity: string;
}
interface AnalysisResult {
    projectName: string; projectType: string; totalPages: number;
    finishingMaterials: FinishingMaterial[];
    greenCertification: GreenCert[];
    bfRequirements: BfReq[];
    structuralRequirements: BfReq[];
    spaceProgramme: SpaceProg[];
    evaluationKeywords: EvalKeyword[];
    criticalIssues: CriticalIssue[];
}

interface NasFile {
    name: string; path: string; size: number;
}

export default function BTLAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.btl;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [parseResult, setParseResult] = useState<ParseResult | null>(null);
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    const [streamText, setStreamText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [stage, setStage] = useState(0);
    const [aiDone, setAiDone] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [nasFiles, setNasFiles] = useState<NasFile[]>([]);
    const [nasConnected, setNasConnected] = useState(false);
    const [showNas, setShowNas] = useState(false);
    const [error, setError] = useState('');
    const [activeSection, setActiveSection] = useState<string>('materials');

    const loadNasFiles = useCallback(async () => {
        try {
            const res = await fetch('/api/btl-agent');
            const data = await res.json();
            setNasConnected(data.nasConnected);
            setNasFiles(data.files || []);
            setShowNas(true);
        } catch { setError('NAS 연결 실패'); }
    }, []);

    const analyzeFile = useCallback(async (file?: File, nasPath?: string) => {
        setError(''); setAnalysis(null); setStreamText(''); setStage(1); setIsProcessing(true); setAiDone(false);
        try {
            const formData = new FormData();
            if (file) formData.append('file', file);
            if (nasPath) formData.append('nasPath', nasPath);

            const res = await fetch('/api/btl-agent', { method: 'POST', body: formData });
            if (!res.ok) { const err = await res.json(); setError(err.error || 'API 오류'); setStage(0); setIsProcessing(false); return; }

            setStage(2);
            const reader = res.body?.getReader();
            if (!reader) return;
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.substring(6));
                        if (data.type === 'parse_complete') { setParseResult(data as ParseResult); setStage(3); }
                        else if (data.type === 'chunk') { fullText += data.text; setStreamText(fullText); }
                        else if (data.type === 'complete') {
                            try {
                                const jsonMatch = data.fullText.match(/\{[\s\S]*\}/);
                                if (jsonMatch) setAnalysis(JSON.parse(jsonMatch[0]));
                            } catch { /* text only */ }
                            setStage(4);
                            setAiDone(true);
                        }
                    } catch { /* skip */ }
                }
            }
        } catch (e: any) { setError(e.message || '분석 실패'); setStage(0); }
        finally { setIsProcessing(false); }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && /\.pdf$/i.test(file.name)) analyzeFile(file);
        else setError('PDF 파일(.pdf)만 지원합니다');
    }, [analyzeFile]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) analyzeFile(file);
    }, [analyzeFile]);

    const severityColor = (s: string) => {
        if (s === 'high') return isDark ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50';
        if (s === 'medium') return isDark ? 'text-amber-400 bg-amber-500/10' : 'text-amber-600 bg-amber-50';
        return isDark ? 'text-gray-400 bg-gray-500/10' : 'text-gray-500 bg-gray-100';
    };

    const SECTIONS = [
        { key: 'materials', label: '마감자재', icon: <Landmark className="w-3.5 h-3.5" />, count: analysis?.finishingMaterials?.length || 0 },
        { key: 'green', label: '녹색건축', icon: <Leaf className="w-3.5 h-3.5" />, count: analysis?.greenCertification?.length || 0 },
        { key: 'bf', label: 'BF인증', icon: <Users className="w-3.5 h-3.5" />, count: analysis?.bfRequirements?.length || 0 },
        { key: 'structure', label: '구조/내진', icon: <Building2 className="w-3.5 h-3.5" />, count: analysis?.structuralRequirements?.length || 0 },
        { key: 'space', label: '공간프로그램', icon: <Layers className="w-3.5 h-3.5" />, count: analysis?.spaceProgramme?.length || 0 },
        { key: 'keywords', label: '심사키워드', icon: <Target className="w-3.5 h-3.5" />, count: analysis?.evaluationKeywords?.length || 0 },
    ];

    return (
        <AgentPageLayout config={config} icon={<Landmark className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            <AnimatePresence>
                {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className={`mb-4 p-3 rounded-xl flex items-center gap-2 text-sm ${isDark ? 'bg-red-500/10 text-red-300 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
                        <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                    </motion.div>
                )}
            </AnimatePresence>

            {stage === 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Drag & Drop */}
                    <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-all ${
                            dragOver ? isDark ? `border-amber-400 ${config.accentBg}` : 'border-amber-400 bg-amber-50'
                            : isDark ? 'border-white/10 bg-gray-900/50 hover:border-amber-500/30' : 'border-gray-200 bg-white hover:border-amber-300'
                        }`}>
                        <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                        <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? 'text-amber-400' : isDark ? 'text-amber-500/30' : 'text-amber-300'}`} />
                        <p className="font-bold mb-1">입찰안내서 PDF 업로드</p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            .pdf 파일을 드래그하거나 클릭하세요
                        </p>
                        <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold ${isDark ? `${config.accentBg} ${config.accentText}` : 'bg-amber-50 text-amber-700'}`}>
                            <FileText className="w-4 h-4" /> PDF 파일 선택
                        </div>
                    </div>

                    {/* NAS Browse */}
                    <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-white shadow-sm'}`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Server className="w-5 h-5 text-orange-400" />
                            <span className="font-bold text-sm">NAS 입찰안내서 탐색</span>
                            {nasConnected && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                        </div>
                        <button onClick={loadNasFiles}
                            className={`w-full py-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 ${isDark ? 'bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 border border-orange-500/20' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200'}`}>
                            <FolderOpen className="w-4 h-4" /> NAS에서 입찰안내서 찾기
                        </button>
                        <AnimatePresence>
                            {showNas && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-3 space-y-1.5 overflow-hidden">
                                    {nasFiles.length === 0 ? (
                                        <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {nasConnected ? '입찰안내서가 없습니다' : 'NAS 미연결 — Finder에서 마운트하세요'}
                                        </p>
                                    ) : nasFiles.map(f => (
                                        <button key={f.path} onClick={() => analyzeFile(undefined, f.path)}
                                            className={`w-full text-left px-3 py-2.5 rounded-xl text-[11px] transition cursor-pointer ${isDark ? 'hover:bg-white/5 border border-white/5' : 'hover:bg-gray-50 border border-gray-100'}`}>
                                            <div className="flex items-center gap-2">
                                                <FileText className={`w-4 h-4 shrink-0 ${f.name.endsWith('.pdf') ? 'text-red-400' : 'text-blue-400'}`} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold truncate">{f.name}</div>
                                                    <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{(f.size/1024/1024).toFixed(1)} MB</div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Parse Info */}
                    {parseResult && (
                        <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <FileText className="w-5 h-5 text-red-400" />
                                <span className="font-bold text-sm">{parseResult.fileName}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${isDark ? `${config.accentBg} ${config.accentText}` : 'bg-amber-50 text-amber-600'}`}>
                                    {parseResult.pages}페이지 · {(parseResult.textLength/1000).toFixed(0)}K 글자
                                </span>
                            </div>
                            <p className={`text-[10px] line-clamp-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{parseResult.textPreview}</p>
                        </div>
                    )}

                    {/* Streaming */}
                    {stage >= 3 && !analysis && (
                        <div className={`rounded-2xl border p-5 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className={`w-4 h-4 ${config.accentText} animate-pulse`} />
                                <span className="text-sm font-bold">AI 공공건축 심사위원 분석 중...</span>
                            </div>
                            <pre className={`text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {streamText || 'PDF 텍스트를 Gemini에 전송 중...'}
                            </pre>
                        </div>
                    )}

                    {/* Structured Results */}
                    {analysis && (
                        <>
                            {/* Project Header */}
                            <div className={`rounded-2xl border p-4 ${isDark ? `${config.accentBorder} ${config.accentBg}` : 'border-amber-200 bg-amber-50'}`}>
                                <div className="flex items-center gap-3">
                                    <Building2 className={`w-6 h-6 ${config.accentText}`} />
                                    <div>
                                        <h2 className="font-bold text-lg">{analysis.projectName || '프로젝트'}</h2>
                                        <p className={`text-xs ${isDark ? 'text-amber-300/60' : 'text-amber-600'}`}>
                                            {analysis.projectType || 'BTL'} · {analysis.totalPages || parseResult?.pages || ''}페이지
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Critical Issues */}
                            {analysis.criticalIssues && analysis.criticalIssues.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-red-500/20 bg-red-500/5' : 'border-red-200 bg-red-50'}`}>
                                    <div className="px-4 py-2.5 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-red-400" />
                                        <span className="text-xs font-bold text-red-400">핵심 이슈 ({analysis.criticalIssues.length}건)</span>
                                    </div>
                                    <div className={`divide-y ${isDark ? 'divide-red-500/10' : 'divide-red-200'}`}>
                                        {analysis.criticalIssues.map((ci, i) => (
                                            <div key={i} className="px-4 py-2.5">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="text-xs font-bold">{ci.issue}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${severityColor(ci.severity)}`}>{ci.severity?.toUpperCase()}</span>
                                                </div>
                                                <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{ci.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Section Tabs */}
                            <div className={`flex gap-1 p-1 rounded-xl overflow-x-auto ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                {SECTIONS.filter(s => s.count > 0).map(sec => (
                                    <button key={sec.key} onClick={() => setActiveSection(sec.key)}
                                        className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-all cursor-pointer ${
                                            activeSection === sec.key
                                                ? isDark ? 'bg-white/10 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                                                : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                                        }`}>
                                        {sec.icon} {sec.label} <span className={`text-[10px] px-1 rounded ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>{sec.count}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Section Content */}
                            <AnimatePresence mode="wait">
                                {/* 마감자재 */}
                                {activeSection === 'materials' && analysis.finishingMaterials?.length > 0 && (
                                    <motion.div key="mat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                            <span className="text-xs font-bold flex items-center gap-2"><Landmark className={`w-4 h-4 ${config.accentText}`} /> 실별 마감자재 요구사항</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-[10px]">
                                                <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                                    {['실명','바닥','벽','천장','페이지'].map(h => <th key={h} className={`px-3 py-2 font-bold text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>)}
                                                </tr></thead>
                                                <tbody>{analysis.finishingMaterials.map((m, i) => (
                                                    <tr key={i} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'}`}>
                                                        <td className="px-3 py-2 font-semibold">{m.room}</td>
                                                        <td className="px-3 py-2">{m.floor}</td>
                                                        <td className="px-3 py-2">{m.wall}</td>
                                                        <td className="px-3 py-2">{m.ceiling}</td>
                                                        <td className={`px-3 py-2 font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>p.{m.page}</td>
                                                    </tr>
                                                ))}</tbody>
                                            </table>
                                        </div>
                                    </motion.div>
                                )}

                                {/* 녹색건축 */}
                                {activeSection === 'green' && analysis.greenCertification?.length > 0 && (
                                    <motion.div key="green" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                            <span className="text-xs font-bold flex items-center gap-2"><Leaf className="w-4 h-4 text-green-400" /> 녹색건축/제로에너지 인증 요건</span>
                                        </div>
                                        <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                            {analysis.greenCertification.map((g, i) => (
                                                <div key={i} className="px-4 py-3">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-bold">{g.item}</span>
                                                        {g.grade && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600'}`}>{g.grade}</span>}
                                                        <span className={`text-[10px] font-mono ml-auto ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>p.{g.page}</span>
                                                    </div>
                                                    <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{g.requirement}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {/* BF */}
                                {activeSection === 'bf' && analysis.bfRequirements?.length > 0 && (
                                    <motion.div key="bf" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                            <span className="text-xs font-bold flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /> BF(Barrier-Free) 인증 조건</span>
                                        </div>
                                        <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                            {analysis.bfRequirements.map((b, i) => (
                                                <div key={i} className="px-4 py-3 flex justify-between">
                                                    <div><span className="text-xs font-bold">{b.item}</span><p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{b.requirement}</p></div>
                                                    <span className={`text-[10px] font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>p.{b.page}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Structure */}
                                {activeSection === 'structure' && analysis.structuralRequirements?.length > 0 && (
                                    <motion.div key="str" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                            <span className="text-xs font-bold flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-400" /> 구조/내진 설계 기준</span>
                                        </div>
                                        <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                            {analysis.structuralRequirements.map((s, i) => (
                                                <div key={i} className="px-4 py-3 flex justify-between">
                                                    <div><span className="text-xs font-bold">{s.item}</span><p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.requirement}</p></div>
                                                    <span className={`text-[10px] font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>p.{s.page}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Space */}
                                {activeSection === 'space' && analysis.spaceProgramme?.length > 0 && (
                                    <motion.div key="space" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                            <span className="text-xs font-bold flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-400" /> 공간 프로그램</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-[10px]">
                                                <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                                    {['실명','면적','요구사항','페이지'].map(h => <th key={h} className={`px-3 py-2 font-bold text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>)}
                                                </tr></thead>
                                                <tbody>{analysis.spaceProgramme.map((sp, i) => (
                                                    <tr key={i} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                                        <td className="px-3 py-2 font-semibold">{sp.room}</td>
                                                        <td className={`px-3 py-2 font-mono ${config.accentText}`}>{sp.area}</td>
                                                        <td className="px-3 py-2">{sp.requirement}</td>
                                                        <td className={`px-3 py-2 font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>p.{sp.page}</td>
                                                    </tr>
                                                ))}</tbody>
                                            </table>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Keywords */}
                                {activeSection === 'keywords' && analysis.evaluationKeywords?.length > 0 && (
                                    <motion.div key="kw" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Target className="w-4 h-4 text-rose-400" />
                                            <span className="text-xs font-bold">심사 평가 핵심 키워드</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {analysis.evaluationKeywords.map((kw, i) => (
                                                <div key={i} className={`px-3 py-2 rounded-xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="text-xs font-bold">{kw.keyword}</span>
                                                        <span className={`text-[8px] px-1 py-0.5 rounded font-bold ${severityColor(kw.importance)}`}>{kw.importance?.toUpperCase()}</span>
                                                    </div>
                                                    <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{kw.context}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Toolbar */}
                            <AgentToolbar title="BTL 공공건축 분석" content={streamText} agentType="btl" />

                            {/* Reset */}
                            <div className="flex justify-center pt-2">
                                <button onClick={() => { setStage(0); setParseResult(null); setAnalysis(null); setStreamText(''); setAiDone(false); }}
                                    className={`px-6 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition ${isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                    <RefreshCw className="w-3.5 h-3.5 inline mr-2" /> 새 PDF 분석
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </AgentPageLayout>
    );
}
